import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ComponentProgressDetailView,
  OverrideQuizAnswerRequest,
  PauseQuizResponseView,
} from '@/types/coaching'
import { PartialDataFallback } from './parts/PartialDataFallback'
import { CoachingVideoPlayer, type PauseMarker } from './parts/CoachingVideoPlayer'
import {
  QuestionChipGrid,
  type QuestionChipItem,
  type QuestionChipState,
} from './parts/QuestionChipGrid'
import { AdultQuestionView } from './parts/AdultQuestionView'
import { AnswerEditButton, AnswerEditCancelButton } from './parts/AnswerEditButton'
import { SolutionPreviewToggle } from './parts/SolutionPreviewToggle'
import { isSubjectiveQuizType } from './parts/SubjectiveGradeSelect'
import { hasSubmittedAnswer } from './parts/answerArray'
import { useSolutionPreviewState } from './parts/useSolutionPreviewState'
import { getQuizReviewMetadata, QuizReviewMetadata } from './parts/QuizReviewMetadata'
import { formatPauseTime } from './formatPauseTime'
import type { QuizFocusRequest } from '../grading-sheet/coachingFocus'

interface Props {
  component: ComponentProgressDetailView
  canEdit: boolean
  onOverride: (body: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => void | Promise<void>
  isSubmitting?: boolean
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
  /** 마운트 시 미리 선택할 pause 문항(채점표 [답안 편집] 바로가기) */
  initialSelectedQuizId?: string | null
  /** 마운트 이후 도착한 선택 요청 — nonce가 바뀌면 해당 문항으로 이동·seek */
  focusRequest?: QuizFocusRequest | null
}

function resolveBackendUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined
  if (url.startsWith('http')) return url
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
  if (apiBaseUrl.startsWith('http')) {
    return `${new URL(apiBaseUrl).origin}${url}`
  }
  return `${window.location.origin}${url}`
}

function pauseResponseState(response: PauseQuizResponseView | undefined): QuestionChipState {
  if (!response) return 'unanswered'
  if (response.isCorrect == null) return 'pending'
  if (response.partialCredit != null && response.partialCredit > 0 && response.partialCredit < 1) {
    return 'partial'
  }
  return response.isCorrect ? 'correct' : 'incorrect'
}

// 영상 컴포넌트 상세 = 영상 플레이어 + 일시정지 문제(있을 때).
// 일시정지 문제도 일반 문제와 동일하게 제출 답안은 읽기 전용, 미제출 답안은 풀이 UI가 기본이다.
export function VideoCoachingDetail({
  component,
  canEdit,
  onOverride,
  isSubmitting,
  initialSelectedQuizId = null,
  focusRequest = null,
  draftResetKey,
  onDraftStateChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const video = component.video
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(initialSelectedQuizId)
  const [editingQuizIds, setEditingQuizIds] = useState<Set<string>>(new Set())
  const [dismissedPendingEditorIds, setDismissedPendingEditorIds] = useState<Set<string>>(new Set())
  const [locallySubmittedIds, setLocallySubmittedIds] = useState<Set<string>>(new Set())
  const {
    isOpen: isPreviewOpen,
    open: openPreview,
    close: closePreview,
    rememberDraft,
    getDraft,
    clear: clearPreview,
  } = useSolutionPreviewState('pause', component.configComponentId, {
    draftResetKey, onDraftStateChange, saving: isSubmitting,
    savedAnswers: Object.fromEntries((video?.pauseQuizResponses ?? []).map((response) => [response.quizId, response.studentAnswer])),
  })
  const [seekTrigger, setSeekTrigger] = useState<
    { pauseTimeMs: number; nonce: number } | undefined
  >(undefined)

  const responses = video?.pauseQuizResponses ?? []
  const pausePoints = video?.pausePoints ?? []
  const showPauseUI = (video?.pauseEnabled ?? false) || responses.length > 0

  const responsesByQuiz = useMemo(
    () => new Map(responses.map((r) => [r.quizId, r])),
    [responses],
  )
  const orderedPoints = useMemo(
    () => [...pausePoints].sort((a, b) => a.pauseTimeMs - b.pauseTimeMs),
    [pausePoints],
  )

  const markers: PauseMarker[] = useMemo(() => {
    if (!showPauseUI) return []
    return orderedPoints.map<PauseMarker>((p) => {
      const r = responsesByQuiz.get(p.quizId)
      const state: PauseMarker['state'] = pauseResponseState(r)
      return { quizId: p.quizId, pauseTimeMs: p.pauseTimeMs, state }
    })
  }, [orderedPoints, responsesByQuiz, showPauseUI])

  const chips: QuestionChipItem[] = useMemo(
    () =>
      orderedPoints.map((p, i) => {
        const r = responsesByQuiz.get(p.quizId)
        const time = formatPauseTime(p.pauseTimeMs, t)
        return {
          quizId: p.quizId,
          label: `Q${i + 1}·${time}`,
          state: pauseResponseState(r),
        }
      }),
    [orderedPoints, responsesByQuiz, t],
  )

  const effectiveSelectedId =
    selectedQuizId ?? (orderedPoints.length > 0 ? orderedPoints[0].quizId : null)

  const selectAndSeek = useCallback(
    (quizId: string) => {
      const point = orderedPoints.find((p) => p.quizId === quizId)
      if (!point) return
      if (effectiveSelectedId != null) closePreview(effectiveSelectedId)
      setSelectedQuizId(quizId)
      setSeekTrigger({ pauseTimeMs: point.pauseTimeMs, nonce: Date.now() })
    },
    [closePreview, effectiveSelectedId, orderedPoints],
  )

  const focusQuizId = focusRequest?.quizId ?? null
  const focusNonce = focusRequest?.nonce ?? null
  useEffect(() => {
    if (focusQuizId == null || focusNonce == null) return
    if (!orderedPoints.some((p) => p.quizId === focusQuizId)) return
    selectAndSeek(focusQuizId)
    // selectAndSeek/orderedPoints 변화로 재실행되면 안 된다 — 요청(nonce)마다 1회.
  }, [focusQuizId, focusNonce])

  if (!video) return <PartialDataFallback type="video" />
  const resolvedVideoUrl = resolveBackendUrl(video.videoUrl)

  const selectedPoint =
    effectiveSelectedId == null
      ? null
      : orderedPoints.find((p) => p.quizId === effectiveSelectedId) ?? null
  const selectedResponse =
    effectiveSelectedId == null ? undefined : responsesByQuiz.get(effectiveSelectedId)

  const pauseDataMissing = showPauseUI && orderedPoints.length === 0 && responses.length === 0
  const canSolve = canEdit
  const isSubjective = selectedPoint != null && isSubjectiveQuizType(selectedPoint.questionSnapshot)
  const hasAnswer = selectedPoint != null && (
    hasSubmittedAnswer(selectedResponse?.studentAnswer) || locallySubmittedIds.has(selectedPoint.quizId)
  )
  const isPendingSubjectiveGrade = selectedPoint != null
    && isSubjective
    && hasAnswer
    && selectedResponse?.isCorrect == null
  const showDefaultPendingEditor = isPendingSubjectiveGrade
    && !dismissedPendingEditorIds.has(selectedPoint.quizId)
  const showPreview = selectedPoint != null && canSolve && !hasAnswer
    && isPreviewOpen(selectedPoint.quizId)
  const showSolver = selectedPoint != null && canSolve && !showPreview && (
    editingQuizIds.has(selectedPoint.quizId) || !hasAnswer || showDefaultPendingEditor
  )
  const previewPanelId = selectedPoint == null
    ? undefined
    : `solution-preview-pause-${component.configComponentId}-${selectedPoint.quizId}`
  const { correctCriteria, checkPoint } = getQuizReviewMetadata(selectedPoint?.questionSnapshot)

  const startEditing = () => {
    if (!selectedPoint) return
    setEditingQuizIds((prev) => new Set(prev).add(selectedPoint.quizId))
    setDismissedPendingEditorIds((prev) => {
      const next = new Set(prev)
      next.delete(selectedPoint.quizId)
      return next
    })
  }

  const cancelEditing = () => {
    if (!selectedPoint) return
    setEditingQuizIds((prev) => {
      const next = new Set(prev)
      next.delete(selectedPoint.quizId)
      return next
    })
    if (isPendingSubjectiveGrade) {
      setDismissedPendingEditorIds((prev) => new Set(prev).add(selectedPoint.quizId))
    }
  }

  const finishEditing = () => {
    if (!selectedPoint) return
    setEditingQuizIds((prev) => {
      const next = new Set(prev)
      next.delete(selectedPoint.quizId)
      return next
    })
    setLocallySubmittedIds((prev) => new Set(prev).add(selectedPoint.quizId))
    setDismissedPendingEditorIds((prev) => {
      const next = new Set(prev)
      next.delete(selectedPoint.quizId)
      return next
    })
    clearPreview(selectedPoint.quizId)
  }

  const handleSolve = async (answers: string[]) => {
    if (!selectedPoint) return
    try { const submission = onOverride({
      configComponentId: component.configComponentId,
      quizId: selectedPoint.quizId,
      isCorrect: selectedResponse?.isCorrect ?? false, // answers 동반 시 백엔드가 재채점(이 값은 무시됨)
      scope: 'pause',
      pauseTimeMs: selectedPoint.pauseTimeMs,
      answers,
    })
    if (submission) await submission
    finishEditing()
    } catch { /* Mutation reports the error; keep the answer draft available for retry. */ return }
  }

  const handleSubjectiveGrade = async (grade: {
    isCorrect: boolean
    partialCredit: 0 | 0.5 | 1
    answers: string[]
  }) => {
    if (!selectedPoint) return
    try { const submission = onOverride({
      configComponentId: component.configComponentId,
      quizId: selectedPoint.quizId,
      isCorrect: grade.isCorrect,
      scope: 'pause',
      pauseTimeMs: selectedPoint.pauseTimeMs,
      answers: grade.answers,
      partialCredit: grade.partialCredit,
      manualGrade: true,
    })
    if (submission) await submission
    finishEditing()
    } catch { /* Mutation reports the error; keep the answer draft available for retry. */ return }
  }

  return (
    <div className="space-y-3">
      <CoachingVideoPlayer
        videoUrl={resolvedVideoUrl}
        thumbUrl={video.videoThumbUrl}
        markers={markers}
        onMarkerClick={selectAndSeek}
        seekTrigger={seekTrigger}
      />
      {pauseDataMissing ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {t('video.pauseDataMissing')}
        </p>
      ) : null}
      {showPauseUI && chips.length > 0 ? (
        <QuestionChipGrid
          items={chips}
          selectedQuizId={effectiveSelectedId ?? ''}
          onSelect={selectAndSeek}
          ariaLabel={t('video.pauseQuizHeader')}
        />
      ) : null}
      {selectedPoint && canSolve && hasAnswer ? (
        <div className="flex justify-end">
          {showSolver ? (
            <AnswerEditCancelButton onClick={cancelEditing} disabled={isSubmitting} />
          ) : (
            <AnswerEditButton onClick={startEditing} disabled={isSubmitting} />
          )}
        </div>
      ) : null}
      {selectedPoint && canSolve && !hasAnswer && previewPanelId ? (
        <div className="flex justify-end">
          <SolutionPreviewToggle
            expanded={showPreview}
            controlsId={previewPanelId}
            onClick={() => {
              if (showPreview) {
                closePreview(selectedPoint.quizId)
              } else {
                openPreview(selectedPoint.quizId)
              }
            }}
            disabled={isSubmitting}
          />
        </div>
      ) : null}
      {selectedPoint ? (
        showPreview && previewPanelId ? (
          <div id={previewPanelId}>
            <AdultQuestionView
              key={`preview-${selectedPoint.quizId}`}
              mode="preview"
              quizId={selectedPoint.quizId}
              order={0}
              questionSnapshot={selectedPoint.questionSnapshot}
              correctAnswer={selectedPoint.correctAnswer}
              explanation={selectedPoint.explanation}
            />
          </div>
        ) : showSolver ? (
          <AdultQuestionView
            key={`solve-${selectedPoint.quizId}-${isSubjective ? 'subjective' : 'auto'}`}
            mode="solve"
            quizId={selectedPoint.quizId}
            order={0}
            questionSnapshot={selectedPoint.questionSnapshot}
            studentAnswer={getDraft(selectedPoint.quizId) ?? selectedResponse?.studentAnswer}
            correctAnswer={selectedPoint.correctAnswer}
            explanation={selectedPoint.explanation}
            isCorrect={selectedResponse?.isCorrect ?? undefined}
            partialCredit={selectedResponse?.partialCredit}
            corrects={selectedResponse?.corrects}
            onSolve={isSubjective ? undefined : handleSolve}
            onSubjectiveGrade={isSubjective ? handleSubjectiveGrade : undefined}
            allowSubjectivePartialCredit={isSubjective}
            onAnswerChange={(answers) => rememberDraft(selectedPoint.quizId, answers)}
            isSubmitting={isSubmitting}
          />
        ) : (
          <AdultQuestionView
            key={`result-${selectedPoint.quizId}`}
            mode="result"
            quizId={selectedPoint.quizId}
            order={0}
            questionSnapshot={selectedPoint.questionSnapshot}
            studentAnswer={selectedResponse?.studentAnswer}
            correctAnswer={selectedPoint.correctAnswer}
            explanation={selectedPoint.explanation}
            isCorrect={selectedResponse?.isCorrect ?? undefined}
            partialCredit={selectedResponse?.partialCredit}
            corrects={selectedResponse?.corrects}
          />
        )
      ) : null}
      <QuizReviewMetadata correctCriteria={correctCriteria} checkPoint={checkPoint} />
    </div>
  )
}
